import React, { useState, startTransition, useEffect, ChangeEvent } from "react"
import { useCustomEventListener } from "react-custom-events"
import format from "date-fns/format"
import addDays from "date-fns/addDays"
import SearchAvailability, {
  HotelOptionsByCityType,
} from "./../../../../utils/booking_engine"
import {
  CachedSearchParamsType,
  cacheSearchParams,
  cachedSearchParams,
} from "./../../../../utils/search"
import { emitCustomEvent } from "react-custom-events"
// import { scrollToElement } from "../helpers/utils"
import * as styles from "./searchWidget.module.scss"
import { AnimatePresence, motion } from "framer-motion"
import { SearchFormType, SearchWidgetPropsType } from "./types"
import { CalendarParamsType, SearchParamsType } from "../../../calendar/types"
import { MOVE_UP, DEFAULT_ANIMATION } from "../../../../utils/animation"
import { toastMsg } from "../../../../utils/notifications"
import { parseQueryParams, sp_to_qs } from "../../../../utils/url"
import { tagExists } from "../../../../utils/hotel"
import useIsMobile from "../../../is_mobile"
import { DEFAULT_SELECT_STYLES } from "../../../../utils/forms"

const Calendar = React.lazy(() => import("./../../../calendar"))
const Spinner = React.lazy(() => import("./../../../spinner"))
const Input = React.lazy(() => import("./../../input"))
const ButtonElement = React.lazy(() => import("./../../button"))
const Icon = React.lazy(() => import("./../../icon"))
const Button = React.lazy(() => import("./../../../button"))
const Select = React.lazy(() => import("../../select"))

const SearchWidget = ({
  data,
  pageParams,
  ...props
}: SearchWidgetPropsType) => {
  const isMobile = useIsMobile()
  const queryParams = parseQueryParams(props.location.search)
  const [config, setConfig] = useState(data.config)

  const getParam = (
    cache: CachedSearchParamsType | null,
    paramName: keyof CachedSearchParamsType,
    defaultValue: any | undefined
  ) => {
    // Return params from URL
    if (queryParams[paramName] !== undefined) {
      return queryParams[paramName]
    }
    // Return property value
    if (props[paramName] !== undefined) {
      return props[paramName]
    }
    // Return params from Cache
    if (cache) {
      return cache[paramName]
    }
    return defaultValue
  }

  const defaultSearchParams = (): CachedSearchParamsType => {
    const cachedParams = cachedSearchParams()
    return {
      check_in: getParam(cachedParams, "check_in", new Date()),
      check_out: getParam(cachedParams, "check_out", addDays(new Date(), 1)),
      adult_count: getParam(cachedParams, "adult_count", 2),
      child_count: getParam(cachedParams, "child_count", 0),
      promo_codes: getParam(cachedParams, "promo_codes", []),
      rate_codes: getParam(cachedParams, "rate_codes", []),
    }
  }

  const [searchParams, setSearchParams] = useState<SearchParamsType>({
    hotel_codes: data.hotel_codes,
    selected_hotel: null,
    ...defaultSearchParams(),
  })
  const [hotelOptions, _setHotelOptions] = useState<
    HotelOptionsByCityType[] | null
  >(data.hotelOptions)
  const [searchForm, _setSearchForm] = useState<SearchFormType | null>(
    data.search_form
  )
  const [spinner, setSpinner] = useState<boolean>(false)

  function redirectToPage() {
    if (!searchParams.selected_hotel || !data.hotelPages) {
      startTransition(() => toastMsg("Please choose location first", "error"))
      return
    }
    window.location.href = targetPage(
      data.hotelPages[searchParams.selected_hotel].link
    )
  }

  function runSearch() {
    startTransition(() => setSpinner(true))
    if (props.beforeSearch) {
      props.beforeSearch(searchParams)
    }
    emitCustomEvent("loading-availability", searchParams)
    cacheSearchParams(searchParams)
    SearchAvailability(
      searchParams.check_in,
      searchParams.check_out,
      searchParams.adult_count,
      searchParams.child_count,
      searchParams.promo_codes,
      searchParams.rate_codes,
      searchParams.hotel_codes
    ).then((result: any) => {
      emitCustomEvent("render-availability", {
        hotels: result?.length ? result : [],
        searchParams: searchParams,
      })
      startTransition(() => setSpinner(false))
    })
  }

  const targetPage = (path: string) =>
    `${path.replace(/\/$/, "")}/${sp_to_qs(
      searchParams.check_in,
      searchParams.check_out,
      searchParams.adult_count,
      searchParams.child_count,
      searchParams.rate_codes,
      searchParams.promo_codes
    )}`

  function handleSearchAvailability() {
    runSearch()
    // Add search parameters into the query params (permalink)
    window.history.pushState(null, "", targetPage(props.location.pathname))
  }

  // INIT
  useEffect(() => {
    // Load availability once initialized
    if (tagExists(pageParams.tags, "hotel-profile", "city", "city", "deal")) {
      runSearch()
    }
  }, [])

  useCustomEventListener("close-calendar", (data: CalendarParamsType) => {
    startTransition(() => {
      setSearchParams({
        ...searchParams,
        selected_hotel: data.selected_hotel
          ? data.selected_hotel
          : searchParams.selected_hotel,
        check_in: data.startDate,
        check_out: data.endDate,
        adult_count: data.adult_count,
        child_count: data.child_count,
        promo_codes: data.promo_codes.length
          ? data.promo_codes
          : searchParams.promo_codes,
      })
      setConfig({ ...config, showCalendar: false })
    })
  })

  return (
    <div className={`${styles.search_widget} ${props.className ?? ""}`}>
      <AnimatePresence>
        {!!searchForm && (
          <div
            className={`sw-widget${config.showLocations ? " location" : ""}`}
            {...DEFAULT_ANIMATION}
          >
            {/* On mobile display only the Hotel select with a popup */}
            {config.showLocations &&
              (isMobile ? (
                <div
                  className="search-popup"
                  onClick={() => {
                    startTransition(() =>
                      setConfig({ ...config, showCalendar: true })
                    )
                  }}
                >
                  <div>
                    {data.hotelPages && searchParams.selected_hotel
                      ? data.hotelPages[searchParams.selected_hotel].name
                      : "Select a hotel"}
                    <Icon icon={{ icon: "chevron-right", group: "FAS" }} />
                  </div>
                  <span>SEARCH</span>
                </div>
              ) : (
                !!hotelOptions && (
                  <Select
                    className={"sw-hotels"}
                    name="sw-hotels"
                    placeholder={searchForm.hotels?.placeholder}
                    label={searchForm.hotels?.label}
                    options={hotelOptions}
                    value={
                      data.hotelPages && searchParams.selected_hotel
                        ? {
                            label:
                              data.hotelPages[searchParams.selected_hotel].name,
                            value: searchParams.selected_hotel,
                          }
                        : null
                    }
                    styles={{
                      ...DEFAULT_SELECT_STYLES,
                      groupHeading: (baseStyles: any) => ({
                        ...baseStyles,
                        textTransform: "none",
                        fontSize: "16px",
                        fontWeight: 600,
                      }),
                      option: (baseStyles: any, state: any) => {
                        return {
                          ...DEFAULT_SELECT_STYLES.option(baseStyles, state),
                          cursor: "pointer",
                          padding: "8px 12px 0 30px",
                        }
                      },
                      group: (baseStyles: any) => ({
                        ...baseStyles,
                        paddingBottom: "20px",
                      }),
                    }}
                    onChange={(option: any, _) =>
                      startTransition(() =>
                        setSearchParams({
                          ...searchParams,
                          selected_hotel: option.value,
                        })
                      )
                    }
                  />
                )
              ))}
            <div
              className={`sw-dates ${
                config.showLocations && isMobile ? "hide" : ""
              }`}
              onClick={() =>
                startTransition(() =>
                  setConfig({ ...config, showCalendar: true })
                )
              }
            >
              <label>
                <span>{searchForm.check_in.label}</span>
                {format(searchParams.check_in, "iii, MMM d yyyy")}
              </label>
              <label>
                <span>{searchForm.check_out.label}</span>
                {format(searchParams.check_out, "iii, MMM d yyyy")}
              </label>
            </div>
            <div
              className={`sw-group ${
                config.showLocations && isMobile ? "hide" : ""
              }`}
            >
              <div
                className="sw-guests"
                onClick={() =>
                  startTransition(() =>
                    setConfig({ ...config, showGuests: true })
                  )
                }
              >
                <label>
                  <span>{searchForm.guests.label}</span>
                </label>
                {searchParams.adult_count + searchParams.child_count}
              </div>
              {!props.hidePromocode && (
                <Input
                  className="sw-promo"
                  {...searchForm.promo_code}
                  value={
                    searchParams.promo_codes.length
                      ? searchParams.promo_codes[0]
                      : ""
                  }
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    startTransition(() =>
                      setSearchParams({
                        ...searchParams,
                        promo_codes: e.target.value ? [e.target.value] : [],
                      })
                    )
                  }
                />
              )}
            </div>
            <AnimatePresence>
              {config.showGuests && (
                <motion.div className="guests-modal" {...MOVE_UP}>
                  <div className="inner">
                    <div>
                      <div className="text">
                        <div className="title">
                          {searchForm.adult_count.label}
                        </div>
                        <div className="desc">Ages 13 or above</div>
                      </div>
                      <div className="buttons">
                        <div
                          className="minus"
                          onClick={() =>
                            startTransition(() =>
                              setSearchParams({
                                ...searchParams,
                                adult_count: Math.max(
                                  1,
                                  --searchParams.adult_count
                                ),
                              })
                            )
                          }
                        ></div>{" "}
                        <div className="qty">{searchParams.adult_count}</div>
                        <div
                          className="plus"
                          onClick={() =>
                            startTransition(() =>
                              setSearchParams({
                                ...searchParams,
                                adult_count: ++searchParams.adult_count,
                              })
                            )
                          }
                        ></div>
                      </div>
                    </div>
                    <div>
                      <div className="text">
                        <div className="title">
                          {searchForm.child_count.label}
                        </div>
                        <div className="desc">Ages 2-12</div>
                      </div>
                      <div className="buttons">
                        <div
                          className="minus"
                          onClick={() =>
                            startTransition(() =>
                              setSearchParams({
                                ...searchParams,
                                child_count: Math.max(
                                  0,
                                  --searchParams.child_count
                                ),
                              })
                            )
                          }
                        ></div>
                        <div className="qty">{searchParams.child_count}</div>
                        <div
                          className="plus"
                          onClick={() =>
                            startTransition(() =>
                              setSearchParams({
                                ...searchParams,
                                child_count: ++searchParams.child_count,
                              })
                            )
                          }
                        ></div>
                      </div>
                    </div>
                  </div>
                  <Button
                    className="btn3"
                    type="button"
                    onClick={() =>
                      startTransition(() =>
                        setConfig({ ...config, showGuests: false })
                      )
                    }
                  >
                    Done
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
            <div
              className={`search-button ${
                config.showLocations && isMobile ? "hide" : ""
              }`}
            >
              {spinner ? (
                <Spinner color="#00b5ee" />
              ) : (
                <ButtonElement
                  className="btn1"
                  {...{
                    ...searchForm.submit,
                    value: props.submitButtonText
                      ? props.submitButtonText
                      : searchForm.submit.value,
                  }}
                  onClick={() =>
                    config.showLocations
                      ? redirectToPage()
                      : handleSearchAvailability()
                  }
                />
              )}
            </div>
          </div>
        )}
      </AnimatePresence>
      <Calendar
        showCalendar={!!config.showCalendar}
        showDates={config.showDates}
        showGuests={config.showCalendarGuests}
        showHotels={config.showLocations && isMobile}
        hotelPages={
          data.hotelPages && config.showLocations && isMobile
            ? data.hotelPages
            : undefined
        }
        showSummary={config.showLocations && isMobile}
        location={props.location}
        {...searchParams}
      />
    </div>
  )
}

export default SearchWidget
